/**
 * This Angular Authentication Guard guards all configured requests
 * and inspects the URL and matches the URL against a database of
 * configured tentant (Client) urls and then forwards the authentication
 * to the proper connector in Auth0
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, CanActivate, Router } from '@angular/router';
import { AuthService, IdToken } from '@auth0/auth0-angular';
import { Observable, of, catchError, from } from 'rxjs';
import { switchMap, map, first } from 'rxjs/operators';
import { ConfigService } from 'app/core/services/config.service';
import { AuthInitializerService } from './auth-initializer.service';
import { HttpClient } from '@angular/common/http';

import { CurrentUserService } from '../current-user/current-user.service';
import { AlertService } from '../alerts/alert.service';
import { clearAuthLocalState, isRunningInIframe, sanitizeAuthTarget } from './auth-cleanup';
import { TenantSsoResolver, TenantSsoInfo } from './tenant-sso-resolver';
import { SelfHostedAuthService } from './self-hosted-auth.service';

/**
 * Public-facing embed/share/request paths that do not require admin-panel
 * access — Viewer-role and SSO-provisioned users without admin access must
 * still reach them (e.g. filling out a request form).
 */
const NON_ADMIN_PATHS = [
  '/client/requests/new',
  '/client/requests/feedback/',
  '/client/boards/embed',
  '/client/boards/share',
  '/client/companies/embed',
  '/client/stories/embed',
  '/client/tracking/share',
  '/client/surveys',
  '/client/prompts',
];

const proto = window.location.protocol + '//';
const host = window.location.hostname + (window.location.port ? ':' + window.location.port : '');


@Injectable({providedIn: 'root'})
export class AuthGuard implements CanActivate {

  constructor(
    private auth: AuthService,
    private userService: CurrentUserService,
    private router: Router,
    private authInitializer: AuthInitializerService,
    private configService: ConfigService,
    private http: HttpClient,
    private alertService: AlertService,
    private ssoResolver: TenantSsoResolver,
    private selfHostedAuth: SelfHostedAuthService
  ) {}
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> {

    // Self-hosted session (#16114): a stored SlapFive JWT short-circuits the
    // Auth0 SDK chain entirely. When only the shared-domain session-cookie
    // hint exists (fresh tenant origin, or the 15m JWT lapsed), try minting a
    // tenant JWT first — the s5_session cookie is tenant-agnostic, so this
    // applies even on non-selfHosted tenants. A failed resume falls through
    // to the normal Auth0 chain rather than forcing a redirect, which would
    // loop forever against an existing Auth0 session.
    if (this.selfHostedAuth.hasSession()) {
      return from(this.selfHostedAuth.restoreSession()).pipe(
        switchMap(token =>
          token ? this.handleSelfHostedUser(state) : this.checkAuth0Session(state)
        )
      );
    }

    return this.checkAuth0Session(state);
  }

  private checkAuth0Session(state: RouterStateSnapshot): Observable<boolean> {
    return this.auth.isLoading$.pipe(
      first(isLoading => !isLoading), // Wait until loading is false
      switchMap(() => this.auth.isAuthenticated$),
      switchMap(isAuthenticated => {
        if (isAuthenticated) {
          return this.handleAuthenticatedUser(state);
        }
        return this.handleLogin(state);
      })
    );
  }

  /**
   * Handle post-authentication logic for an authenticated user, like role checks.
   */
  private handleAuthenticatedUser(state: RouterStateSnapshot): Observable<boolean> {
    return this.auth.idTokenClaims$.pipe(
      first(),
      switchMap<IdToken, Observable<boolean>>((idToken) => {
        // This turned out to be the best way to hardcode some client requested url mappings; redirect accordingly:
        if (window.location.href === "https://stories.qlik.com/") {
          window.location.href = "https://stories.qlik.com/b/ckusi0qsn0000389rq2ptkpy7/VIDEO_STORYBOARD_LIBRARY"
        }
        if (window.location.href === "https://casestudies.acronis.com/") {
          window.location.href = "https://www.acronis.com/en-us/resource-center/category/case-studies-274/"
        }

        if (!idToken) {
          console.warn('AuthGuard: Authenticated but no ID token found. Re-authenticating.');
          return this.handleLogin(state);
        }

        const isSuperadmin: boolean = !!idToken['https://app.slapfive.com/superadmin'];

        // Defensive: break stale password sessions on SSO-only tenants. If a user
        // once logged in via password, the Auth0 SDK can silently return a password
        // token for a tenant subdomain even though the tenant has disabled password
        // auth. Force them back through the SSO connection so the tenant's identity
        // provider is always consulted.
        if (!isSuperadmin && idToken.sub?.startsWith('auth0|')) {
          const ssoInfo = this.readStoredSsoInfo();
          if (ssoInfo?.ssoOnly) {
            console.warn(
              `AuthGuard: cached password session (${idToken.sub}) on SSO-only tenant ` +
              `(${ssoInfo.connection || ssoInfo.provider}), forcing SSO re-login`
            );
            // Self-hosted SAML tenant (#16117): re-login goes through the
            // self-hosted SP initiate endpoint, not Auth0.
            if (ssoInfo.selfHosted && ssoInfo.provider === 'saml' && ssoInfo.initiateUrl) {
              window.location.assign(`${ssoInfo.initiateUrl}?returnTo=${encodeURIComponent(sanitizeAuthTarget(state.url))}`);
              return of(false);
            }
            return this.performLogin(state, { authGuard: { auth0ConnectionName: ssoInfo.connection } });
          }
          // No stored SSO info means the tenant has no configured/enforced SSO
          // connection (or the API lookup failed to persist it). Do not derive a
          // connection name from the hostname — that caused bad redirects to
          // non-existent `<subdomain>-sso` connections for password tenants.
        }

        const role = idToken['https://app.slapfive.com/role'];
        if (role) {
          // Block users whose role lacks admin panel access from the admin app.
          // The 'admin-access' claim (https://app.slapfive.com/admin-access) is
          // set to permission_admin_app from the DB — false for Viewer
          // and any custom role without admin access. The /access-denied route is
          // excluded from the guard so it is always reachable.
          // admin-access claim is only present in tokens minted after the
          // enrichToken update. If the claim is absent (old token), allow
          // through — the user will naturally re-auth on next session and
          // pick up the correct claim. Only block when explicitly false.
          const adminAccessClaim = idToken['https://app.slapfive.com/admin-access'];
          const hasAdminAccess: boolean = isSuperadmin || adminAccessClaim === undefined || !!adminAccessClaim;
          // These paths are public-facing embed/share/request pages — they must
          // NOT require admin panel access. Viewer-role and SSO-provisioned users
          // without admin access need to reach them (e.g. filling out a request form).
          // Strip query and fragment strings before matching. handleLogin already
          // does this for the sso-lookup call; handleAuthenticatedUser should too,
          // otherwise a URL like /client/stories/embed?x=1 fails the exact match
          // and is incorrectly treated as an admin path.
          const urlForPathChecks = state.url.split('?')[0].split('#')[0];
          const isNonAdminPath = NON_ADMIN_PATHS.some(p => {
            if (urlForPathChecks === p) return true;
            if (p.endsWith('/')) return urlForPathChecks.startsWith(p);
            return urlForPathChecks.startsWith(p + '/');
          });
          const isAdminPath = !isNonAdminPath && (urlForPathChecks.startsWith('/client') || urlForPathChecks === '/' || urlForPathChecks === '');
          const isAccessDeniedPath = urlForPathChecks.startsWith('/access-denied');
          if (!hasAdminAccess && isAdminPath && !isAccessDeniedPath) {
            const roleName: string = (role as any)?.role || 'unknown';
            console.warn(`AuthGuard: Role "${roleName}" has no admin panel access — blocking "${state.url}". Redirecting to access-denied.`);
            this.router.navigate(['/access-denied'], { queryParams: { reason: 'viewer' }, replaceUrl: true });
            return of(false);
          }

          // Automatically set Grafana authentication cookie on every page load/navigation
          // SKIP if navigating to logout to prevent recreating cookies during logout
          if (!state.url.includes('/logout')) {
            this.setGrafanaAuthCookie().subscribe({
              error: (error) => console.warn('Failed to set Grafana auth cookie:', error)
            });
          }

          return of(true);
        }

        // Interruptive error handling: missing role claim is a silent config failure that
        // causes an infinite logout->re-login loop for SSO users. Surface the error
        // visibly so customers and support can diagnose the IdP mapping issue.
        const ssoConnection = sessionStorage.getItem('sso_connection');
        const userEmail = idToken.email || (idToken as any).name || 'unknown';
        const userSub = idToken.sub || 'unknown';
        const errorDetail = `Missing role claim for user ${userEmail} (${userSub})` +
          (ssoConnection ? ` via SSO connection "${ssoConnection}".` : '.');

        console.error('AuthGuard: ' + errorDetail);
        this.alertService.error(
          `Unable to sign in: your account is missing a required role assignment. ` +
            `This usually means the SSO identity provider is not sending an email address ` +
            `that matches your SlapFive account, or the Auth0 post-login action did not run. ` +
            `Please contact your administrator with this info: ${errorDetail}`,
          { timeout: 0 } // Persistent alert
        );

        // Set the same circuit-breaker that AuthCallbackComponent uses so that
        // the next navigation attempt does not immediately re-trigger SSO.
        sessionStorage.setItem('sso_auth_error', JSON.stringify({
          connection: ssoConnection || 'unknown',
          target: state.url,
          timestamp: Date.now(),
          error: 'missing_role_claim',
          description: errorDetail
        }));

        clearAuthLocalState(this.userService);
        this.auth.logout({
          logoutParams: {
            returnTo: `${window.location.protocol}//${window.location.host}/logout`
          }
        });
        return of(false);
      })
    );
  }

  /**
   * Authenticated path for self-hosted sessions (#16114): decode the stored
   * SlapFive JWT and apply the same role/admin-access checks the Auth0 path
   * performs against idTokenClaims.
   */
  private handleSelfHostedUser(state: RouterStateSnapshot): Observable<boolean> {
    const claims = this.selfHostedAuth.getClaims();
    if (!claims) {
      this.selfHostedAuth.clear();
      return this.handleLogin(state);
    }

    const isSuperadmin = !!claims['https://app.slapfive.com/superadmin'];
    const role = claims['https://app.slapfive.com/role'];

    if (!role) {
      console.error('AuthGuard: self-hosted token missing role claim — clearing session');
      this.selfHostedAuth.clear();
      this.alertService.error(
        'Unable to sign in: your account is missing a required role assignment. Please contact your administrator.',
        { timeout: 0 }
      );
      return this.handleLogin(state);
    }

    const adminAccessClaim = claims['https://app.slapfive.com/admin-access'];
    const hasAdminAccess: boolean = isSuperadmin || adminAccessClaim === undefined || !!adminAccessClaim;
    const urlForPathChecks = state.url.split('?')[0].split('#')[0];
    const isNonAdminPath = NON_ADMIN_PATHS.some(p => {
      if (urlForPathChecks === p) return true;
      if (p.endsWith('/')) return urlForPathChecks.startsWith(p);
      return urlForPathChecks.startsWith(p + '/');
    });
    const isAdminPath = !isNonAdminPath && (urlForPathChecks.startsWith('/client') || urlForPathChecks === '/' || urlForPathChecks === '');
    const isAccessDeniedPath = urlForPathChecks.startsWith('/access-denied');
    if (!hasAdminAccess && isAdminPath && !isAccessDeniedPath) {
      const roleName: string = (role as any)?.role || 'unknown';
      console.warn(`AuthGuard: self-hosted role "${roleName}" has no admin panel access — blocking "${state.url}".`);
      this.router.navigate(['/access-denied'], { queryParams: { reason: 'viewer' }, replaceUrl: true });
      return of(false);
    }

    if (!state.url.includes('/logout')) {
      this.setGrafanaAuthCookie().subscribe({
        error: (error) => console.warn('Failed to set Grafana auth cookie:', error)
      });
    }

    return of(true);
  }

private readStoredSsoInfo(): TenantSsoInfo | null {
  try {
    const raw = sessionStorage.getItem('sso_info');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

private storeSsoInfo(info: TenantSsoInfo): void {
  try {
    sessionStorage.setItem('sso_info', JSON.stringify(info));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Handle the login process, checking for SSO configuration
 */
private handleLogin(state: RouterStateSnapshot): Observable<boolean> {
  // Circuit-breaker: if a recent SSO auth error occurred, don't re-trigger the redirect loop.
  // The AuthCallbackComponent sets this flag when the IDP returns an error (e.g., SAML AuthnFailed).
  try {
    const ssoAuthError = sessionStorage.getItem('sso_auth_error');
    if (ssoAuthError) {
      const errorData = JSON.parse(ssoAuthError);
      const ageMs = Date.now() - (errorData.timestamp || 0);
      // Circuit-breaker active for 5 minutes after a failed SSO attempt
      if (ageMs < 5 * 60 * 1000) {
        console.warn(`AuthGuard: SSO circuit-breaker active for connection "${errorData.connection}" (${Math.round(ageMs / 1000)}s ago). Blocking redirect loop.`);
        return of(false);
      }
      // Expired — clean up
      sessionStorage.removeItem('sso_auth_error');
    }
  } catch (e) {
    // Ignore parse errors
  }

  // Use the state.url to determine the actual page we are trying to access,
  // rather than the static window.location.pathname which might be stale (e.g. during redirects from root)
  const targetPath = state.url.split('?')[0];
  const checkUrl = proto + host + targetPath;

  // Resolve the connection via the shared resolver (REST lookup + hostname fallback).
  return this.ssoResolver.lookup(checkUrl).pipe(
    switchMap((ssoInfo) => {
      console.log(`AuthGuard: SSO lookup for ${checkUrl} returned:`, JSON.stringify(ssoInfo));
      this.storeSsoInfo(ssoInfo);

      // Self-hosted tenant (#16114): unauthenticated users land on the local
      // login page instead of the Auth0 universal login. auth_target_url is
      // the same hand-off performLogin uses; the login page reads it after
      // minting the session token. First try the shared-domain s5_session
      // cookie — a session from a sibling tenant subdomain resumes without a
      // password prompt (#4960 phase 4).
      if (ssoInfo.selfHosted) {
        sessionStorage.setItem('auth_target_url', sanitizeAuthTarget(state.url));
        return from(this.selfHostedAuth.restoreSession()).pipe(
          switchMap(token => {
            if (token) {
              return this.handleSelfHostedUser(state);
            }
            // SAML-only tenant (password disabled): SP-initiated login —
            // redirect straight to the IdP via the self-hosted initiateUrl.
            // Tenants with password auth enabled land on the local login
            // form, whose SSO button hits the same initiateUrl.
            if (ssoInfo.provider === 'saml' && ssoInfo.initiateUrl && !ssoInfo.passwordEnabled) {
              const returnTo = sanitizeAuthTarget(state.url);
              window.location.assign(`${ssoInfo.initiateUrl}?returnTo=${encodeURIComponent(returnTo)}`);
              return of(false);
            }
            this.router.navigate(['/auth/login'], { replaceUrl: true });
            return of(false);
          })
        );
      }

      const customAuthGuard = ssoInfo.connection
        ? { authGuard: { auth0ConnectionName: ssoInfo.connection } }
        : null;

      // When the tenant enforces SSO (SP-initiated login, username/password
      // disabled), do not blindly force the tenant's IdP. A user may already
      // hold a valid Auth0 session on the shared auth domain — most importantly
      // superadmins (CSMs) switching between client instances, who frequently
      // have no SSO account in the tenant's IdP. The localStorage cache is
      // per-origin, so isAuthenticated$ is false on a freshly-loaded tenant
      // subdomain even when an Auth0 session exists. Attempt a silent token
      // first; if it succeeds, handleAuthenticatedUser will verify the session
      // is actually SSO (and force re-login if it is a stale password session).
      if (customAuthGuard?.authGuard?.auth0ConnectionName) {
        return from(this.auth.getAccessTokenSilently()).pipe(
          switchMap(token => {
            if (token) {
              return this.handleAuthenticatedUser(state);
            }

            // preferSso: show login chooser interstitial instead of immediate IdP redirect
            if (ssoInfo.preferSso) {
              return this.navigateToSsoChooser(state, customAuthGuard.authGuard.auth0ConnectionName);
            }

            return this.performLogin(state, customAuthGuard);
          }),
          catchError(() => {
            // preferSso: show login chooser interstitial instead of immediate IdP redirect
            if (ssoInfo.preferSso) {
              return this.navigateToSsoChooser(state, customAuthGuard!.authGuard.auth0ConnectionName);
            }
            return this.performLogin(state, customAuthGuard);
          })
        );
      }

      return this.performLogin(state, customAuthGuard);
    })
  );
}

/**
 * Perform the actual login redirect
 */
private performLogin(state: RouterStateSnapshot, customAuthGuard: any): Observable<boolean> {
  // Preserve the full URL including subdomain and path. Sanitized so an
  // auth-flow-only route (e.g. /logout) can never become the post-login
  // redirect target - see sanitizeAuthTarget().
  const targetPath = sanitizeAuthTarget(state.url);
  const fullTargetUrl = `${window.location.protocol}//${window.location.host}${targetPath}`;

  // Generate callback URL dynamically to preserve the original domain for deep linking
  // This ensures proper CSP compliance and user experience
  const callbackUrl = `${window.location.protocol}//${window.location.host}/auth/callback`;

  // Check for login_hint in URL to pass context to Auth0
  const params = new URLSearchParams(window.location.search);
  const loginHint = params.get('login_hint');

  const authorizationParams: any = {
    redirect_uri: callbackUrl,
    audience: this.configService.auth0.authorizationParams.audience,
    scope: 'openid profile email offline_access',
    ...(customAuthGuard?.authGuard?.auth0ConnectionName && {
      connection: customAuthGuard.authGuard.auth0ConnectionName
    })
  };

  if (loginHint) {
    authorizationParams.login_hint = loginHint;
    // Also add target_subdomain for legacy support if needed
    if (loginHint.startsWith('subdomain:')) {
      authorizationParams.target_subdomain = loginHint.split(':')[1];
    }
  }

  const loginConfig = {
    appState: {
      target: targetPath,
      fullUrl: fullTargetUrl,
      originalHost: window.location.host,
      originalProtocol: window.location.protocol
    },
    authorizationParams
  };

  // Store comprehensive target information in sessionStorage as a fallback
  // NOTE: This only works for same-subdomain scenarios due to same-origin policy
  // For cross-subdomain scenarios, we rely on Auth0's appState mechanism
  sessionStorage.setItem('auth_target_url', targetPath);
  sessionStorage.setItem('auth_full_url', fullTargetUrl);
  sessionStorage.setItem('auth_original_host', window.location.host);
  sessionStorage.setItem('auth_original_protocol', window.location.protocol);
  
  // Store SSO connection for use by logged-out page's "Log In Again" button
  if (customAuthGuard?.authGuard?.auth0ConnectionName) {
    sessionStorage.setItem('sso_connection', customAuthGuard.authGuard.auth0ConnectionName);
  } else {
    sessionStorage.removeItem('sso_connection');
  }

  // Salesforce Experience Cloud (and some other CRM) iframes enforce CSP
  // frame-ancestors 'none', which blocks Auth0's redirect flow. Popup login
  // is the workaround, but it must be opened from a user click — auto-calling
  // loginWithPopup() here is blocked by the browser (SLAPFIVE-FRONTEND-A2X).
  // SessionStorage already holds the target URL and SSO connection; the
  // embed-login page opens the popup from its Sign in button.
  if (isRunningInIframe()) {
    this.router.navigate(['/auth/embed-login'], { replaceUrl: true });
    return of(false);
  }

  // Use the auth initializer to ensure we're properly tracking the auth state
  this.authInitializer.setRedirectInProgress(true);

  return from(this.auth.loginWithRedirect(loginConfig)).pipe(
    catchError(error => {
      console.error('Error during redirect login:', error);
      return of(false);
    }),
    map(() => false) // Redirect authentication - page will navigate away
  );
}

  /**
   * Navigate to the SSO chooser interstitial page instead of immediately
   * redirecting to the IdP. Used when a tenant has preferSso enabled (password
   * login remains available as a fallback).
   */
  private navigateToSsoChooser(state: RouterStateSnapshot, connectionName: string): Observable<boolean> {
    sessionStorage.setItem('sso_prefer_connection', connectionName);
    sessionStorage.setItem('auth_target_url', state.url);
    this.router.navigate(['/sso-chooser'], { replaceUrl: true });
    return of(false);
  }

  /**
   * Automatically set Grafana authentication cookie using current JWT token
   */
  private setGrafanaAuthCookie(): Observable<any> {
    // Self-hosted sessions (#16114) have no Auth0 session — use the stored
    // SlapFive JWT instead of getAccessTokenSilently, which would fail with
    // "Login required" on every navigation.
    const selfHostedToken = this.selfHostedAuth.getToken();
    const tokenSource: Observable<string | null> = selfHostedToken
      ? of(selfHostedToken)
      : this.auth.getAccessTokenSilently().pipe(
          catchError(err => {
            console.warn('AuthGuard: Failed to get token silently for Grafana cookie', err);
            return of(null);
          })
        );
    return tokenSource.pipe(
      switchMap(token => {
        if (!token) {
          return of(null);
        }

        return this.http.post('/api/auth/grafana-login-silent', {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }),
      catchError(error => {
        console.warn('Failed to set Grafana auth cookie silently:', error);
        return of(null);
      })
    );
  }

}